import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Mascot } from "../brand/Mascot";
import { HandNote } from "../brand/Doodles";

const CAPABILITIES = [
  "Business Automations",
  "Internal Tools",
  "CRM Integrations",
  "AI Agents",
  "Custom Web Apps",
  "Voice AI",
  "Multi-Agent Systems",
];

export const TwoWays = () => {
  return (
    <section
      id="two-ways"
      data-testid="two-ways-section"
      className="relative"
      style={{ paddingTop: "clamp(64px, 10vh, 140px)", paddingBottom: "clamp(64px, 10vh, 140px)" }}
    >
      {/* keep legacy anchor compatibility */}
      <span id="services" className="block relative -top-24" aria-hidden />

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl mb-14">
          <HandNote testId="two-ways-eyebrow" className="text-lg mb-4">Two ways to work with me</HandNote>
          <h2
            className="font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
            data-testid="two-ways-headline"
          >
            Two Ways to Turn Your Idea Into Reality.
          </h2>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr] items-stretch" data-testid="two-ways-cards">
          {/* Card 1 — primary offer, slightly heavier */}
          <article
            data-testid="two-ways-card-build"
            className="relative flex flex-col rounded-md p-8 md:p-10"
            style={{
              background: "var(--ggm-surface)",
              border: "2px solid var(--ggm-signal-cyan)",
              boxShadow: "8px 8px 0 var(--ggm-orbit-blue)",
            }}
          >
            <HandNote className="text-lg mb-4">01 · Build it for me</HandNote>
            <h3
              className="font-condensed font-bold text-white"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", lineHeight: 1.05 }}
              data-testid="two-ways-card-build-title"
            >
              Done-For-You AI Builds
            </h3>
            <p
              className="mt-4 font-condensed font-bold text-white/90"
              style={{ fontSize: "clamp(1.1rem, 2vw, 1.35rem)", lineHeight: 1.3 }}
            >
              You bring the problem. I build the solution.
            </p>

            <p className="mt-5 font-body text-white/75 leading-relaxed">
              Need an AI automation that actually runs your workflow?
            </p>
            <p className="mt-2 font-body text-white/75 leading-relaxed">
              Lead intake. Follow-up. Scheduling. Reporting. Inbox triage. Internal
              tools your team uses every day.
            </p>
            <p className="mt-2 font-body text-white/85 leading-relaxed">
              I build custom AI systems around your business—fast.
            </p>

            <ul
              className="mt-6 grid grid-cols-2 gap-x-6 gap-y-1.5 font-body text-sm text-white/80"
              data-testid="two-ways-capabilities"
            >
              {CAPABILITIES.map((c) => (
                <li key={c} className="flex gap-2">
                  <span className="text-[var(--ggm-signal-cyan)]">+</span>
                  {c}
                </li>
              ))}
            </ul>

            <p className="mt-6 font-body text-white/65 text-sm leading-relaxed">
              From idea to working product without the agency timeline, overhead, or
              runaround.
            </p>

            <div
              className="mt-6 p-4"
              style={{
                background: "rgba(40,200,240,0.06)",
                borderLeft: "3px solid var(--ggm-signal-cyan)",
              }}
              data-testid="two-ways-card-build-bonus"
            >
              <p className="font-body text-white/85 text-sm leading-relaxed">
                <span className="font-condensed uppercase tracking-widest text-[11px] text-[var(--ggm-signal-cyan)] block mb-1">
                  Bonus
                </span>
                For builds booked by Dec 31, I'll include 12 months of hosting + basic
                maintenance (a $75/mo value), then $75/mo after year one.
              </p>
              <p className="mt-2 font-body text-white/55 text-xs leading-relaxed">
                Domains, third-party tools, and usage-based AI/API costs are separate.
              </p>
            </div>

            <div className="mt-8 flex items-end justify-between gap-6 flex-wrap">
              <a
                href="#start-a-project"
                className="ggm-btn-primary"
                data-testid="two-ways-cta-build"
              >
                Start a build <ArrowRight size={16} />
              </a>

              <div className="hidden md:block shrink-0 -mb-4" style={{ width: 150, transform: "rotate(4deg)" }}>
                <Mascot pose="laptop" size="md" className="mx-auto" />
              </div>
            </div>
          </article>

          {/* Card 2 — coaching */}
          <article
            data-testid="two-ways-card-coach"
            className="relative flex flex-col rounded-md p-8 md:p-10 lg:mt-10 lg:rotate-[0.8deg]"
            style={{
              background: "transparent",
              border: "2px dashed rgba(230,237,247,0.3)",
            }}
          >
            <HandNote className="text-lg mb-4" tilt={1.5}>02 · Show me how</HandNote>
            <h3
              className="font-condensed font-bold text-white"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", lineHeight: 1.05 }}
              data-testid="two-ways-card-coach-title"
            >
              Vibe Coding Coaching
            </h3>
            <p
              className="mt-4 font-condensed font-bold text-white/90"
              style={{ fontSize: "clamp(1.1rem, 2vw, 1.35rem)", lineHeight: 1.3 }}
            >
              Want to build it yourself? I'll show you exactly how I do it.
            </p>

            <p className="mt-5 font-body text-white/75 leading-relaxed">
              You don't need to become a traditional programmer to start building with AI.
            </p>
            <p className="mt-2 font-body text-white/75 leading-relaxed">
              I'll work with you one-on-one to take your idea from concept to working
              application, agent, or automation using the same AI-powered development
              approach I use to build my own products.
            </p>
            <p className="mt-2 font-body text-white/75 leading-relaxed">
              Bring an idea, a project you're stuck on, or just the desire to learn.
            </p>

            <div
              className="mt-8 inline-flex items-baseline gap-2 px-4 py-3 self-start"
              style={{
                background: "var(--ggm-tech-light)",
                transform: "rotate(-3deg)",
                boxShadow: "0 10px 20px -12px rgba(0,0,0,0.6)",
              }}
              data-testid="two-ways-pricing"
            >
              <span
                className="font-condensed font-bold text-[var(--ggm-orbit-blue)]"
                style={{ fontSize: 28, lineHeight: 1 }}
              >
                $75
              </span>
              <span className="font-condensed uppercase tracking-widest text-xs" style={{ color: "var(--ggm-navy)" }}>
                / hour
              </span>
            </div>

            <div className="mt-8 flex items-end justify-between gap-6 flex-wrap">
              <Link to="/pricing" className="ggm-btn-secondary" data-testid="two-ways-cta-coach">
                Book Coaching <ArrowRight size={16} />
              </Link>

              <div className="hidden md:block shrink-0 -mb-4" style={{ width: 130, transform: "rotate(-4deg)" }}>
                <Mascot pose="tablet" size="md" className="mx-auto" />
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default TwoWays;
